import Hls from "hls.js";
import { useEffect, useRef } from "react";
import { isIgnorablePlayError, useMediaElementEvents, type PlayerCallbacks } from "./mediaEvents.js";

/**
 * HLS video + audio players.
 *
 * Playback strategy:
 *  1. On Apple WebKit (Safari, iOS, the iOS WebView) use native HLS — it's
 *     what makes AirPlay and iOS picture-in-picture work.
 *  2. Everywhere else prefer `hls.js` (Android WebView, Chrome/Edge,
 *     Firefox). Recent Chromium also *claims* native HLS support, but its
 *     demuxer rejects streams hls.js plays fine, so it only gets used when
 *     MSE isn't available at all.
 *  3. If neither path applies, report a fatal error so `StationPlayer`
 *     can move on to the next URL.
 *
 * These players only *report* failures; deciding whether to reconnect or
 * switch URL is `StationPlayer`'s job (see playbackPolicy.ts).
 */

function isAppleWebKit(): boolean {
  return typeof window !== "undefined" && "WebKitPlaybackTargetAvailabilityEvent" in window;
}

interface HlsPlayerProps extends PlayerCallbacks {
  src: string;
  autoPlay?: boolean;
  paused?: boolean;
  /** Native browser controls. Off on TV — a D-pad can't reach them (see PlayerControls). */
  controls?: boolean;
}

function useHls(src: string, autoPlay: boolean, paused: boolean, cb: PlayerCallbacks) {
  const ref = useRef<HTMLVideoElement | HTMLAudioElement | null>(null);
  const cbRef = useRef(cb);
  cbRef.current = cb;
  useMediaElementEvents(ref, cb, src);

  const reportPlayError = (err: unknown) => {
    if (isIgnorablePlayError(err)) return;
    cbRef.current.onFatal?.({ message: (err as Error)?.message ?? String(err), network: false });
  };

  // Real pause/resume (e.g. from the MediaSession notification) without
  // tearing down and reattaching hls.js — that's what the effect below,
  // keyed on `src`, is for.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (paused) el.pause();
    else void el.play().catch(reportPlayError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paused]);

  useEffect(() => {
    const el = ref.current;
    if (!el || !src) return;

    const nativeOk = el.canPlayType("application/vnd.apple.mpegurl") !== "";
    if (nativeOk && (isAppleWebKit() || !Hls.isSupported())) {
      el.src = src;
      if (autoPlay) void el.play().catch(reportPlayError);
      return () => {
        el.pause();
        el.removeAttribute("src");
        el.load();
      };
    }

    // hls.js path
    if (Hls.isSupported()) {
      const hls = new Hls({ enableWorker: true });
      let mediaRecoveries = 0;
      hls.loadSource(src);
      hls.attachMedia(el as HTMLMediaElement);
      const onHlsError = (_evt: unknown, data: { fatal?: boolean; type?: string; details?: string }) => {
        if (!data.fatal) return;
        // A decode hiccup is usually recoverable in place — try once before
        // giving up on the URL.
        if (data.type === Hls.ErrorTypes.MEDIA_ERROR && mediaRecoveries < 1) {
          mediaRecoveries++;
          hls.recoverMediaError();
          return;
        }
        cbRef.current.onFatal?.({
          message: `hls fatal: ${data.details ?? "unknown"}`,
          network: data.type === Hls.ErrorTypes.NETWORK_ERROR,
        });
      };
      hls.on(Hls.Events.ERROR, onHlsError);
      if (autoPlay) hls.on(Hls.Events.MANIFEST_PARSED, () => void el.play().catch(reportPlayError));
      return () => {
        hls.off(Hls.Events.ERROR, onHlsError);
        hls.destroy();
      };
    }

    cbRef.current.onFatal?.({ message: "hls not supported in this browser", network: false });
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, autoPlay]);

  return ref;
}

export function HlsVideoPlayer({ src, autoPlay = true, paused = false, controls = true, ...cb }: HlsPlayerProps) {
  const ref = useHls(src, autoPlay, paused, cb) as React.MutableRefObject<HTMLVideoElement | null>;
  return (
    <video
      ref={ref}
      controls={controls}
      playsInline
      // AirPlay on iOS WebKit; harmless elsewhere.
      x-webkit-airplay="allow"
      className="w-full h-full bg-black object-contain"
    />
  );
}

export function HlsAudioPlayer({ src, autoPlay = true, paused = false, controls = true, ...cb }: HlsPlayerProps) {
  const ref = useHls(src, autoPlay, paused, cb) as React.MutableRefObject<HTMLAudioElement | null>;
  return <audio ref={ref} controls={controls} className="w-full" />;
}
